import Box from "@mui/material/Box";
import BreadCrumb from "app/components/BreadCrumb";
import { quotesSaga } from "app/containers/Quotes/redux/saga";
import {
  quotesActions,
  reducer as quotesReducer,
  sliceKey as quotesSliceKey,
} from "app/containers/Quotes/redux/slice";
import clsx from "clsx";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { useInjectReducer, useInjectSaga } from "redux-injectors";
import {
  selectActiveDetailTab,
  selectWorkOrderVehicleDetails,
} from "../../redux/selector";
import { actions, reducer, sliceKey } from "../../redux/slice";
import { workOrderSaga } from "../../redux/saga";
import DetailTabs from "./DetailTabs/index";
import BoxDetails from "./BoxDetails";
import AddOn from "./AddOn";
import CentreOfGravity from "./CentreofGravity";
import DiagramView from "./DiagramView";
import Pictures from "./Pictures";
import Quotation from "./Quotation";
import SummaryBar from "./SummaryBar";
import VehicleDetails from "./VehicleDetails";
import WeightAndWall from "./WeightAndWall";
import LoadDistribution from "./LoadDistribution";
import { getTextDirection } from "./VehicleDetails/FormControls";
import { getMockVehicleDetails } from "./data";
import { useStyles } from "./styles";
import { WorkOrderDetailTab } from "./types";

const WorkOrderDetails = () => {
  const { t, i18n } = useTranslation();
  const styles = useStyles();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();

  useInjectReducer({
    key: sliceKey,
    reducer,
  });
  useInjectSaga({ key: sliceKey, saga: workOrderSaga });
  useInjectReducer({
    key: quotesSliceKey,
    reducer: quotesReducer,
  });
  useInjectSaga({ key: quotesSliceKey, saga: quotesSaga });

  const activeTab = useSelector(selectActiveDetailTab);
  const vehicleDetails = useSelector(selectWorkOrderVehicleDetails);
  const workOrderId = Number(id);

  useEffect(() => {
    if (!Number.isNaN(workOrderId)) {
      dispatch(actions.getWorkOrderDetails(workOrderId));
      dispatch(quotesActions.getQuoteDetails(String(workOrderId)));
    }

    return () => {
      dispatch(actions.clearWorkOrderDetails());
      dispatch(quotesActions.clearQuoteDetails());
    };
  }, [dispatch, workOrderId]);

  const summaryData =
    vehicleDetails ?? getMockVehicleDetails(workOrderId);

  const renderTabContent = () => {
    switch (activeTab) {
      case WorkOrderDetailTab.PRICE_QUOTE:
        return <Quotation />;
      case WorkOrderDetailTab.VEHICLE_DETAILS:
        return <VehicleDetails />;
      case WorkOrderDetailTab.CENTER_OF_GRAVITY:
        return <CentreOfGravity />;
      case WorkOrderDetailTab.BOX_DETAILS:
        return <BoxDetails />;
      case WorkOrderDetailTab.ADDONS:
        return <AddOn />;
      case WorkOrderDetailTab.WEIGHT_DATA:
        return <WeightAndWall />;
      case WorkOrderDetailTab.LOAD_DISTRIBUTION:
        return <LoadDistribution />;
      case WorkOrderDetailTab.PICTURES:
        return <Pictures />;
      default:
        return null;
    }
  };

  return (
    <Box className={styles.container}>
        <Box className={styles.pageHeader} dir={i18n.dir()}>
          <BreadCrumb
            title={t("WORK_ORDER.ADD")}
            subTitle={t("WORK_ORDER.DETAILS_TITLE")}
            onBackClick={() => navigate("/work-orders")}
          />
        </Box>

        <Box className={styles.scrollContent} dir={i18n.dir()}>
          <Box className={styles.pageContent}>
            <Box className={styles.detailsSection}>
              <SummaryBar data={summaryData} />
              <Box className={styles.detailsCard}>
                <DetailTabs
                  value={activeTab}
                  onChange={(tab) => dispatch(actions.setActiveDetailTab(tab))}
                />
                <Box className={styles.tabContent}>
                  <Box className={styles.tabLayout}>
                    <Box className={styles.diagramPane}>
                      <DiagramView />
                    </Box>
                    <Box
                      className={clsx(
                        styles.formPane,
                        activeTab === WorkOrderDetailTab.PRICE_QUOTE &&
                          styles.formPaneQuotation,
                        activeTab === WorkOrderDetailTab.CENTER_OF_GRAVITY &&
                          styles.formPaneCentreOfGravity,
                        activeTab === WorkOrderDetailTab.LOAD_DISTRIBUTION &&
                          styles.formPaneLoadDistribution
                      )}
                      dir={getTextDirection(i18n.language)}
                    >
                      {renderTabContent()}
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
    </Box>
  );
};

export default WorkOrderDetails;
